import React from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { InputRef } from 'antd';
import { Controller } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import {
  AppButton,
  AppCheckboxFormLabel,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import { PostalCodeFields } from '@/components/molecules';
import type { Hospital } from '@/pages/main/hospital/types';
import type { ConsentFormData } from '@/schemas/consentSchema';
import {
  fullWidthToHalfWidthAlphaNum,
  fullWidthToHalfWidthNumber,
  normalizeAddress,
  normalizeTelephoneOrFaxInput,
} from '@/utils';
import type { HospitalModalMode, LabelChecks, SetLabelChecks } from '../types';

interface MedicalInstitutionFieldsProps {
  control: Control<ConsentFormData>;
  disabled: boolean;
  doctorOptions: Array<{ label: string; value: number }>;
  handleHospitalAddressSearch: () => void;
  hospital: Hospital | null;
  hospitalAddressRef: MutableRefObject<InputRef | null>;
  hospitalId?: number | null;
  isHospitalPostalSearching: boolean;
  labelChecks: LabelChecks;
  setHospitalModalMode: Dispatch<SetStateAction<HospitalModalMode>>;
  setIsHospitalModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsHospitalSearchModalOpen: Dispatch<SetStateAction<boolean>>;
  setLabelChecks: SetLabelChecks;
  setValue: UseFormSetValue<ConsentFormData>;
}

export const MedicalInstitutionFields: React.FC<MedicalInstitutionFieldsProps> = ({
  control,
  disabled,
  doctorOptions,
  handleHospitalAddressSearch,
  hospital,
  hospitalAddressRef,
  hospitalId,
  isHospitalPostalSearching,
  labelChecks,
  setHospitalModalMode,
  setIsHospitalModalOpen,
  setIsHospitalSearchModalOpen,
  setLabelChecks,
  setValue,
}) => {
  const setHospitalPostalAddressChecks = (checked: boolean) => {
    setLabelChecks(prev => ({
      ...prev,
      hospital_postal_code: checked,
      hospital_address: checked,
    }));
  };

  return (
    <>
      {/* 4. Medical Institution */}
      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="医療機関名"
          required
          checked={!!labelChecks.hospital_name}
          onChange={e => setLabelChecks(prev => ({ ...prev, hospital_name: e.target.checked }))}
          className="flex-1"
        />
      </div>
      <div className="flex items-start min-w-0 gap-2">
        <div className="w-1/2 min-w-0">
          <Controller
            name="hospital_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="flex-1 mb-0"
              >
                <AppTextField
                  {...field}
                  className="w-full"
                  maxLength={255}
                  status={error ? 'error' : ''}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={() => {
                    const raw = field.value || '';
                    field.onChange(fullWidthToHalfWidthAlphaNum(fullWidthToHalfWidthNumber(raw)));
                  }}
                />
              </AppForm.Item>
            )}
          />
        </div>
        <AppButton
          type="primary"
          onClick={() => setIsHospitalSearchModalOpen(true)}
          disabled={disabled}
        >
          医療機関
        </AppButton>
      </div>

      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="医師選択"
          required
          checked={!!labelChecks.doctor_name}
          onChange={e => setLabelChecks(prev => ({ ...prev, doctor_name: e.target.checked }))}
          className="flex-1"
        />
      </div>
      <div className="flex min-w-0 gap-2">
        <div className="flex w-1/2 min-w-0 gap-2">
          <Controller
            name="doctor_id"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="flex-1 min-w-0 mb-0"
              >
                <AppSelect
                  {...field}
                  options={doctorOptions}
                  className="w-full min-w-0"
                  allowClear
                  disabled={!hospitalId || (!!hospital && doctorOptions.length === 0)}
                  onChange={val => {
                    field.onChange(val);
                    const selectedDoc = hospital?.doctors.find(d => d.id === val);
                    if (selectedDoc) {
                      setValue(
                        'doctor_name',
                        fullWidthToHalfWidthAlphaNum(
                          fullWidthToHalfWidthNumber(selectedDoc.name)
                        ),
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        }
                      );
                    }
                  }}
                />
              </AppForm.Item>
            )}
          />
          <Controller
            name="doctor_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="flex-1 min-w-0 mb-0"
              >
                <AppTextField {...field} maxLength={255} status={error ? 'error' : ''} />
              </AppForm.Item>
            )}
          />
        </div>
        <AppButton
          className="w-fit shrink-0"
          type="primary"
          disabled={!hospitalId || disabled}
          onClick={() => {
            setHospitalModalMode('edit');
            setIsHospitalModalOpen(true);
          }}
        >
          医師新規追加
        </AppButton>
      </div>

      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="郵便番号"
          checked={!!labelChecks.hospital_postal_code}
          onChange={e => setHospitalPostalAddressChecks(e.target.checked)}
          className="flex-1"
        />
      </div>
      <Controller
        name="hospital_postal_code1"
        control={control}
        render={({ field: pc1Field, fieldState: pc1State }) => (
          <Controller
            name="hospital_postal_code2"
            control={control}
            render={({ field: pc2Field, fieldState: pc2State }) => (
              <PostalCodeFields
                postalCode1={pc1Field.value || ''}
                postalCode2={pc2Field.value || ''}
                disabled={disabled}
                onPostalCode1Change={next => pc1Field.onChange(next)}
                onPostalCode2Change={next => pc2Field.onChange(next)}
                onPostalCode2Autofill={next =>
                  setValue('hospital_postal_code2', next, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                errorPostalCode1={pc1State.error?.message}
                errorPostalCode2={pc2State.error?.message}
                onSearch={handleHospitalAddressSearch}
                searchLoading={isHospitalPostalSearching}
              />
            )}
          />
        )}
      />

      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="住所"
          required
          checked={!!labelChecks.hospital_address}
          onChange={e => setHospitalPostalAddressChecks(e.target.checked)}
          className="flex-1"
        />
      </div>
      <div className="flex items-start w-1/2 min-w-0 gap-4">
        <div className="flex-[2] min-w-0">
          <Controller
            name="hospital_address"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="mb-0"
              >
                <AppTextField
                  {...field}
                  ref={node => {
                    hospitalAddressRef.current = node;
                    if (typeof field.ref === 'function') field.ref(node);
                  }}
                  value={field.value || ''}
                  maxLength={255}
                  onBlur={() => {
                    field.onChange(normalizeAddress(field.value || ''));
                  }}
                />
              </AppForm.Item>
            )}
          />
        </div>
        <div className="flex items-start flex-1 min-w-0 gap-2">
          <AppFormLabel label="電話番号" className="pt-1 whitespace-nowrap" />
          <Controller
            name="hospital_telephone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
                className="flex-1 mb-0"
              >
                <AppTextField
                  {...field}
                  value={field.value || ''}
                  maxLength={13}
                  onChange={e => {
                    const val = normalizeTelephoneOrFaxInput(e.target.value);
                    field.onChange(val);
                  }}
                  onBlur={() => {
                    const val = normalizeTelephoneOrFaxInput(field.value || '');
                    field.onChange(val);
                  }}
                />
              </AppForm.Item>
            )}
          />
        </div>
      </div>
    </>
  );
};
